"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { ArrowRight, Menu, Orbit, Sparkles, Workflow, X, Zap } from "lucide-react";

import GradualBlur from "@/components/ui/gradual-blur";

const Beams = dynamic(() => import("@/components/ui/beams"), {
  ssr: false,
  loading: () => <StaticBeamBackdrop />,
});

const FEATURE_ITEMS = [
  {
    icon: Workflow,
    title: "Local AI workflows",
    body: "Run small-LLM experiments locally with a cleaner workflow and less overhead.",
  },
  {
    icon: Orbit,
    title: "Open model experimentation",
    body: "Test open models quickly and stay flexible while exploring practical small-model setups.",
  },
  {
    icon: Zap,
    title: "Fast iteration loops",
    body: "Move from idea to usable small-LLM prototypes fast without unnecessary complexity.",
  },
];

const NAV_ITEMS = [
  { href: "#about", label: "About" },
  { href: "#features", label: "Features" },
  { href: "https://huggingface.co/Atlas-x", label: "Hugging Face", external: true },
];

const MOBILE_NAV_ID = "atlas-x-mobile-nav";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const canUseWebGL = () => {
  if (typeof document === "undefined") return false;

  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
};

const StaticBeamBackdrop: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      <div className="absolute -top-24 left-[8%] h-[120%] w-36 -rotate-[24deg] bg-gradient-to-b from-white/18 via-white/[0.03] to-white/12 opacity-55" />
      <div className="absolute -top-32 left-[42%] h-[130%] w-48 -rotate-[24deg] bg-gradient-to-b from-white/[0.07] via-white/24 to-white/[0.03] opacity-70" />
      <div className="absolute -top-28 right-[10%] h-[120%] w-44 -rotate-[24deg] bg-gradient-to-b from-white/[0.03] via-white/16 to-white/[0.02] opacity-50" />
    </div>
  );
};

interface NavigationProps {
  onInternalLinkClick: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ onInternalLinkClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigationRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        navigationRef.current &&
        !navigationRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 768px)");
    const handleDesktopChange = () => {
      if (desktopQuery.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    handleDesktopChange();
    desktopQuery.addEventListener("change", handleDesktopChange);

    return () => desktopQuery.removeEventListener("change", handleDesktopChange);
  }, []);

  return (
    <div className="fixed inset-x-0 top-0 z-40 flex justify-center px-4 pt-4 sm:px-6 lg:pt-6">
      <div ref={navigationRef} className="w-full max-w-6xl">
        <nav
          aria-label="Primary navigation"
          className={`grid w-full grid-cols-[auto_1fr_auto] items-center rounded-[28px] border px-5 py-3 backdrop-blur-2xl transition-[background-color,border-color,box-shadow] duration-300 sm:px-6 ${
            isScrolled
              ? "border-white/[0.12] bg-black/[0.54] shadow-[0_18px_50px_rgba(0,0,0,0.38)]"
              : "border-white/[0.1] bg-black/[0.4] shadow-[0_12px_30px_rgba(0,0,0,0.24)]"
          }`}
        >
          <a
            href="#top"
            aria-label="Atlas X home"
            className="flex items-center rounded-full px-1 py-1 text-white transition hover:text-white/90"
            onClick={(event) => {
              setIsMobileMenuOpen(false);
              onInternalLinkClick(event, "#top");
            }}
          >
            <div className="flex h-8 w-8 items-center justify-center overflow-hidden">
              <Image
                src="/favicon-96x96.png"
                alt="Atlas X logo"
                width={96}
                height={96}
                className="h-5 w-5 object-contain brightness-0 invert"
                priority
              />
            </div>
          </a>

          <div className="hidden items-center justify-self-center md:flex md:gap-10">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className="text-sm font-medium text-white/74 transition duration-200 hover:text-white"
                onClick={(event) => {
                  if (!item.external) {
                    onInternalLinkClick(event, item.href);
                  }
                }}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              ref={mobileMenuButtonRef}
              type="button"
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-controls={MOBILE_NAV_ID}
              aria-expanded={isMobileMenuOpen}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white transition hover:bg-white/[0.08] md:hidden"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
            >
              {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            <div aria-hidden="true" className="hidden h-10 w-10 md:block" />
          </div>
        </nav>

        <div
          aria-hidden={!isMobileMenuOpen}
          className={`overflow-hidden transition-[max-height,opacity,margin] duration-300 md:hidden ${
            isMobileMenuOpen ? "mt-3 max-h-64 opacity-100" : "mt-0 max-h-0 opacity-0"
          }`}
        >
          <div
            id={MOBILE_NAV_ID}
            role="navigation"
            aria-label="Mobile navigation"
            className="rounded-[24px] border border-white/10 bg-black/[0.55] p-3 shadow-[0_16px_40px_rgba(0,0,0,0.3)] backdrop-blur-2xl"
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                tabIndex={isMobileMenuOpen ? undefined : -1}
                className="flex items-center rounded-[18px] px-4 py-3 text-sm font-medium text-white/82 transition hover:bg-white/[0.06] hover:text-white"
                onClick={(event) => {
                  setIsMobileMenuOpen(false);
                  if (!item.external) {
                    onInternalLinkClick(event, item.href);
                  }
                }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface HeroContentProps {
  onInternalLinkClick: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
}

const HeroContent: React.FC<HeroContentProps> = ({ onInternalLinkClick }) => {
  return (
    <div className="relative z-20 flex min-h-[100svh] items-center px-4 pb-14 pt-32 sm:px-6 sm:pt-36 lg:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-white/68 backdrop-blur-md">
          <Sparkles className="h-3.5 w-3.5" />
          Small LLMs • Local AI • Fast Experiments
        </div>

        <h1 className="mt-8 max-w-4xl text-balance text-[clamp(2.85rem,7vw,5.45rem)] font-semibold leading-[0.96] tracking-[-0.065em] text-white">
          Exploring the power of small LLMs.
        </h1>

        <p className="mt-7 max-w-3xl text-pretty text-[1.02rem] leading-8 text-white/72 sm:text-[1.08rem]">
          Fast, local, and practical AI experiments without the overhead.
        </p>

        <div className="mt-11 flex flex-col items-center gap-3 sm:flex-row">
          <a
            href="https://huggingface.co/Atlas-x"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-black shadow-[0_14px_36px_rgba(255,255,255,0.08)] transition duration-200 hover:-translate-y-0.5 hover:bg-white/[0.94]"
          >
            View on Hugging Face
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="#about"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3.5 text-sm font-medium text-white transition duration-200 hover:bg-white/[0.09]"
            onClick={(event) => onInternalLinkClick(event, "#about")}
          >
            Explore
          </a>
        </div>
      </div>
    </div>
  );
};

const SupportingSection: React.FC = () => {
  return (
    <section
      id="features"
      className="mx-auto grid w-full max-w-6xl scroll-mt-28 gap-4 px-4 pb-20 pt-4 sm:px-6 lg:grid-cols-3 lg:px-8 lg:pb-24 lg:pt-8"
    >
      {FEATURE_ITEMS.map((item) => (
        <div
          key={item.title}
          className="rounded-[28px] border border-white/[0.1] bg-black/[0.2] p-6 shadow-[0_14px_36px_rgba(0,0,0,0.24)] backdrop-blur-xl"
        >
          <item.icon className="h-5 w-5 text-blue-300" />
          <h2 className="mt-4 text-lg font-semibold tracking-[-0.03em] text-white">
            {item.title}
          </h2>
          <p className="mt-3 text-sm leading-7 text-white/62">{item.body}</p>
        </div>
      ))}
    </section>
  );
};

const AboutSection: React.FC = () => {
  return (
    <section
      id="about"
      className="mx-auto flex w-full max-w-4xl scroll-mt-28 flex-col items-center px-4 pb-20 text-center sm:px-6 lg:px-8 lg:pb-24"
    >
      <div className="w-full rounded-[32px] border border-white/[0.1] bg-black/[0.16] px-6 py-8 shadow-[0_18px_40px_rgba(0,0,0,0.22)] backdrop-blur-xl sm:px-10 sm:py-10">
        <p className="text-sm uppercase tracking-[0.18em] text-blue-300/88">
          Local AI • Open Models • Fast Experiments
        </p>
        <h2 className="mt-5 text-[clamp(1.9rem,4vw,3rem)] font-semibold leading-[1.02] tracking-[-0.05em] text-white">
          A focused space for real AI experimentation.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-white/68">
          Explore Atlas X and start building with local models today.
        </p>
        <a
          href="https://huggingface.co/Atlas-x"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-medium text-white transition duration-200 hover:bg-white/[0.08]"
        >
          Open Hugging Face
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
};

export default function ParticleEffectForHero() {
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(false);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      setIsWebGLAvailable(canUseWebGL() && !prefersReducedMotion());
    });

    return () => cancelAnimationFrame(frameId);
  }, []);

  const handleInternalLinkClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith("#")) return;

    event.preventDefault();

    if (href === "#top") {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
      return;
    }

    const target = document.querySelector<HTMLElement>(href);
    if (!target) return;

    const top = Math.max(target.getBoundingClientRect().top + window.scrollY - 96, 0);
    window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  return (
    <div
      id="top"
      className="relative overflow-hidden bg-[var(--surface-base)] text-[var(--text-primary)] selection:bg-blue-500 selection:text-white"
    >
      <div className="fixed inset-0 z-0 overflow-hidden bg-black">
        {isWebGLAvailable ? (
          <Beams
            beamWidth={2}
            beamHeight={15}
            beamNumber={12}
            lightColor="#ffffff"
            speed={2}
            noiseIntensity={1.75}
            scale={0.2}
            rotation={24}
          />
        ) : (
          <StaticBeamBackdrop />
        )}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(131,177,255,0.18),transparent_30%),linear-gradient(180deg,rgba(0,0,0,0.12)_0%,rgba(0,0,0,0.42)_100%)]" />
      </div>

      <section className="relative isolate min-h-[100svh] overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-28 bg-gradient-to-b from-black via-black/78 to-transparent" />
        <Navigation onInternalLinkClick={handleInternalLinkClick} />
        <HeroContent onInternalLinkClick={handleInternalLinkClick} />
      </section>

      <div className="relative z-10">
        <SupportingSection />
        <AboutSection />
      </div>

      <footer className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-4 border-t border-white/8 px-4 py-8 text-sm text-white/58 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <p>Atlas X • Local AI • Open Models • Fast Experiments</p>
        <div className="flex items-center gap-5">
          <Link href="/privacy" className="transition duration-200 hover:text-white">
            Privacy
          </Link>
          <a
            href="https://huggingface.co/Atlas-x"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 transition duration-200 hover:text-white"
          >
            Hugging Face
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </footer>

      <GradualBlur
        target="page"
        position="bottom"
        height="7rem"
        strength={5}
        divCount={5}
        curve="bezier"
        exponential
        opacity={1}
        zIndex={12}
        className="pointer-events-none"
      />
    </div>
  );
}
